import { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDate, toUtc } from "@/lib/mood";
import { QuickReplies } from "@/components/QuickReplies";
import { ThankButton } from "@/components/ThankButton";

const MAX_THREAD = 20;
const MAX_FOLLOWUPS = 3;

const trailingFrom = (thread, sender) => {
  let n = 0;
  for (let i = thread.length - 1; i >= 0 && thread[i].sender === sender; i--) n++;
  return n;
};

// me: "user" | "supporter" — whose perspective the thread is shown from
export const NoteThread = ({ note, me, otherName, endpoint, onUpdate, onThanked }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const thread = note.thread || [];
  const full = thread.length + 1 >= MAX_THREAD;
  const waiting = me === "supporter" && trailingFrom(thread, "supporter") >= MAX_FOLLOWUPS;

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, { message: text.trim() });
      onUpdate(note.id, data.thread);
      setText("");
      toast.success(`Reply sent to ${otherName}`);
    } catch (err) {
      toast.error(errMsg(err, "Could not send reply"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 space-y-2" data-testid={`note-thread-${note.id}`}>
      {thread.map((m, i) => {
        const mine = m.sender === me;
        return (
          <div key={`${m.created_at}-${m.sender}-${i}`} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`note-thread-msg-${note.id}-${i}`}>
            <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm break-words whitespace-pre-wrap ${mine ? "mm-gradient text-white rounded-br-sm" : "mm-soft rounded-bl-sm"}`}>
              <span className={`text-[11px] block ${mine ? "text-white/80" : "text-mm-muted"}`}>
                {mine ? "You" : otherName} · {fmtDate(toUtc(m.created_at), { month: "short", day: "numeric" })}
              </span>
              {m.message}
              {me === "user" && !mine && note.tough_msgs?.includes(i + 1) && (
                <div className="mt-1.5"><ThankButton noteId={note.id} index={i + 1} name={otherName} done={note.thanked?.includes(i + 1)} hearted={note.thanks_hearted?.includes(i + 1)} onDone={onThanked} /></div>
              )}
            </div>
          </div>
        );
      })}
      {full ? (
        <p className="text-xs text-mm-muted" data-testid={`note-thread-full-${note.id}`}>This conversation has reached its limit.</p>
      ) : waiting ? (
        <p className="text-xs text-mm-muted" data-testid={`note-thread-waiting-${note.id}`}>You've sent a few follow-ups — you can write again once {otherName} replies.</p>
      ) : (
        <>
        <QuickReplies noteId={note.id} onPick={setText} me={me} />
        <form onSubmit={send} className="flex gap-2 pt-1" data-testid={`note-thread-form-${note.id}`}>
          <input className="mm-input !py-2" maxLength={500} value={text} onChange={(e) => setText(e.target.value)}
            placeholder={`Reply to ${otherName}…`} data-testid={`note-thread-input-${note.id}`} />
          <button className="mm-btn !px-4" disabled={busy || !text.trim()} data-testid={`note-thread-send-${note.id}`}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </form>
        </>
      )}
    </div>
  );
};
